/**
 * AgentChatPanel — Shared chat UI for inter-agent dialogue
 *
 * Used in both Tomi and Kaze dashboards to display the live
 * conversation stream between 富の神 and 風の洞察.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Send,
  PlayArrow,
  DeleteOutline,
  AutoAwesome,
} from '@mui/icons-material';

export interface ChatMessage {
  id: string;
  agent: 'tomi' | 'kaze' | 'user';
  content: string;
  timestamp: string;
}

interface AgentChatPanelProps {
  /** Which dashboard this panel is mounted in */
  perspective: 'tomi' | 'kaze';
  /** Fetch conversation history */
  getChat: () => Promise<ChatMessage[]>;
  /** Send a user message */
  sendChat: (message: string) => Promise<ChatMessage[]>;
  /** Trigger autonomous exchange */
  triggerExchange: (initiator: 'tomi' | 'kaze') => Promise<ChatMessage[]>;
  /** Clear conversation */
  clearChat: () => Promise<void>;
}

const AGENT_DISPLAY: Record<string, { name: string; kanji: string; color: string }> = {
  tomi: { name: 'Tomi-no-Kami', kanji: '富の神', color: '#f59e0b' },
  kaze: { name: 'Kaze no Dōsatsu', kanji: '風の洞察', color: '#06b6d4' },
  user: { name: 'Command', kanji: '⌘', color: '#8b5cf6' },
};

export default function AgentChatPanel({
  perspective,
  getChat,
  sendChat,
  triggerExchange,
  clearChat,
}: AgentChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load existing conversation on mount
  useEffect(() => {
    getChat().then((msgs) => {
      setMessages(msgs);
      setInitialLoad(false);
    }).catch(() => setInitialLoad(false));
  }, [getChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setLoading(true);
    try {
      const newMsgs = await sendChat(text);
      setMessages((prev) => [...prev, ...newMsgs]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `err_${Date.now()}`,
        agent: 'user',
        content: `[Error: ${(err as Error).message}]`,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleTrigger = async (initiator: 'tomi' | 'kaze') => {
    if (loading) return;
    setLoading(true);
    try {
      const newMsgs = await triggerExchange(initiator);
      setMessages((prev) => [...prev, ...newMsgs]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: `err_${Date.now()}`,
        agent: 'user',
        content: `[Error: ${(err as Error).message}]`,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    await clearChat();
    setMessages([]);
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (initialLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
      {/* Header controls */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tooltip title="Kaze initiates — reports intelligence to Tomi">
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayArrow />}
            onClick={() => handleTrigger('kaze')}
            disabled={loading}
            sx={{ borderColor: '#06b6d4', color: '#06b6d4', '&:hover': { borderColor: '#22d3ee' } }}
          >
            風 Initiates
          </Button>
        </Tooltip>
        <Tooltip title="Tomi initiates — queries Kaze for intelligence">
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayArrow />}
            onClick={() => handleTrigger('tomi')}
            disabled={loading}
            sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#fbbf24' } }}
          >
            富 Initiates
          </Button>
        </Tooltip>
        <Tooltip title="Auto-exchange: agents continue the dialogue">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesome />}
            onClick={() => handleTrigger(messages.length % 2 === 0 ? 'kaze' : 'tomi')}
            disabled={loading}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#a78bfa' } }}
          >
            Continue
          </Button>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Chip label={`${messages.length} messages`} size="small" variant="outlined" />
        <Tooltip title="Clear conversation">
          <IconButton size="small" onClick={handleClear} sx={{ color: 'text.secondary' }}>
            <DeleteOutline />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages stream */}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: '#0a0e17',
          borderColor: 'rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.5 }}>
              富の神 ↔ 風の洞察
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              Press a button above to start the strategic dialogue,
              or type a prompt below to direct the conversation.
            </Typography>
          </Box>
        )}

        {messages.map((msg) => {
          const display = AGENT_DISPLAY[msg.agent] || AGENT_DISPLAY.user;
          const isSelf = msg.agent === perspective;
          const isUser = msg.agent === 'user';

          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'center' : isSelf ? 'flex-end' : 'flex-start',
                maxWidth: isUser ? '100%' : '85%',
                alignSelf: isUser ? 'center' : isSelf ? 'flex-end' : 'flex-start',
              }}
            >
              {/* Agent label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: display.color, letterSpacing: 0.5 }}
                >
                  {display.kanji}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  {formatTime(msg.timestamp)}
                </Typography>
              </Box>

              {/* Message bubble */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: isUser ? 1 : 2,
                  bgcolor: isUser
                    ? 'rgba(139,92,246,0.1)'
                    : msg.agent === 'tomi'
                    ? 'rgba(245,158,11,0.08)'
                    : 'rgba(6,182,212,0.08)',
                  border: '1px solid',
                  borderColor: isUser
                    ? 'rgba(139,92,246,0.2)'
                    : msg.agent === 'tomi'
                    ? 'rgba(245,158,11,0.15)'
                    : 'rgba(6,182,212,0.15)',
                  maxWidth: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.7,
                    color: 'text.primary',
                    fontSize: isUser ? '0.8rem' : '0.85rem',
                    fontStyle: isUser ? 'italic' : 'normal',
                  }}
                >
                  {msg.content}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Agents deliberating...
            </Typography>
          </Box>
        )}

        <div ref={bottomRef} />
      </Paper>

      {/* Input area */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Direct a question to both agents..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.03)',
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || loading}
          sx={{ color: '#8b5cf6' }}
        >
          <Send />
        </IconButton>
      </Box>
    </Box>
  );
}
